import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { FormField } from "@colab/shared";
import type { AdminFormRow } from "./types";

export type UpdateFieldInput = {
  formId: string;
  fieldId: string;
  field: FormField;
};

type Context = {
  previousDetail: AdminFormRow | undefined;
};

// PATCH /api/admin/forms/:formId/fields/:fieldId
//
// Optimistic — replaces the field in place (whole-field swap so kind
// switches work). The route validates body.field.id === :fieldId.
export function useUpdateField() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, UpdateFieldInput, Context>({
    mutationFn: ({ formId, fieldId, field }) =>
      apiFetch<{ ok: true }>(`/admin/forms/${formId}/fields/${fieldId}`, {
        method: "PATCH",
        body: JSON.stringify({ field }),
      }),
    onMutate: async ({ formId, fieldId, field }) => {
      const detailKey = queryKeys.adminForms.detail(formId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<AdminFormRow>(detailKey);
      queryClient.setQueryData<AdminFormRow>(detailKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          definition: {
            ...old.definition,
            sections: old.definition.sections.map((s) => ({
              ...s,
              fields: s.fields.map((f) => (f.id === fieldId ? field : f)),
            })),
          },
        };
      });
      return { previousDetail };
    },
    onError: (err, { formId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.adminForms.detail(formId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to update field");
    },
    onSettled: (_data, _err, { formId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminForms.detail(formId),
      });
    },
  });
}
